import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { CATEGORIES, LEVELS } from "@/lib/api";
import { Trash2, X, Sparkles, AlertTriangle } from "lucide-react";

const PLACEHOLDER = `Dump everything on your mind...

order gloves for the heavy bag
clean the kitchen
call Paul about work
work on the GHL audit tool
maybe build a gutter business
go to gym with Mason
pay electric bill`;

export default function BrainDump() {
  const [text, setText] = useState("");
  const [items, setItems] = useState(null);
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const sort = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const res = await api.sortBrainDump(text);
      setItems(res.items);
      setSource(res.source);
      if (res.ai_error) {
        toast("AI sorting had trouble. Sorted with the simple engine — you can still edit everything.");
      }
    } catch (e) {
      toast.error("AI sorting had trouble. You can still add items manually.");
    } finally {
      setBusy(false);
    }
  };

  const update = (i, patch) =>
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.saveBrainDump(text, items);
      toast.success(`Saved. ${res.created_tasks} tasks, ${res.created_ideas} ideas parked.`);
      navigate("/");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell title="Brain dump" subtitle="Get it all out. AI sorts it into something usable.">
      {!items ? (
        <div className="nrt-fade">
          <textarea
            data-testid="brain-dump-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={PLACEHOLDER}
            className="w-full h-[42vh] bg-zinc-100 text-zinc-900 rounded-3xl p-5 text-base resize-none focus:outline-none focus:ring-2 focus:ring-[#365458] placeholder:text-zinc-400 shadow-md"
          />
          <Button
            data-testid="brain-dump-sort-button"
            onClick={sort}
            disabled={busy || !text.trim()}
            className="w-full h-16 mt-4 rounded-2xl bg-[#365458] hover:bg-[#2A4346] text-white text-lg font-bold disabled:opacity-40"
          >
            <Sparkles className="w-5 h-5 mr-2" /> {busy ? "Sorting…" : "Sort My Brain Dump"}
          </Button>
          <p className="text-center text-xs text-zinc-600 mt-3">
            Nothing is saved until you review and approve it.
          </p>
        </div>
      ) : (
        <div className="nrt-fade">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-zinc-400">{items.length} items. Edit anything, then save.</p>
            <span
              data-testid="sort-source"
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${
                source === "ai" ? "bg-[#365458] text-white" : "bg-zinc-800 text-zinc-400"
              }`}
            >
              {source === "ai" ? <><Sparkles className="w-3 h-3" /> AI sorted</> : <><AlertTriangle className="w-3 h-3" /> Simple sort</>}
            </span>
          </div>

          <div className="space-y-3" data-testid="sorted-items">
            {items.map((it, i) => (
              <div key={i} className="bg-zinc-100 rounded-2xl p-4 shadow-md" data-testid={`sorted-item-${i}`}>
                <div className="flex items-start gap-2">
                  <input
                    value={it.title}
                    onChange={(e) => update(i, { title: e.target.value })}
                    className="flex-1 bg-transparent text-zinc-900 font-semibold focus:outline-none border-b border-transparent focus:border-zinc-300 pb-1"
                    data-testid={`sorted-item-text-${i}`}
                  />
                  <button
                    onClick={() => remove(i)}
                    data-testid={`sorted-item-delete-${i}`}
                    className="text-zinc-400 hover:text-[#991B1B] shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {it.notes ? (
                  <input
                    value={it.notes}
                    onChange={(e) => update(i, { notes: e.target.value })}
                    className="w-full bg-transparent text-sm text-zinc-500 focus:outline-none mt-1"
                    data-testid={`sorted-item-notes-${i}`}
                  />
                ) : null}

                <div className="flex flex-wrap gap-1.5 mt-3">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      onClick={() => update(i, { category: c, recommended_status: c === "Random idea / parking lot" ? "parking_lot" : "task" })}
                      data-testid={`sorted-item-${i}-cat-${c}`}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                        it.category === c ? "bg-[#365458] text-white" : "bg-zinc-200 text-zinc-600 hover:bg-zinc-300"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-zinc-200">
                  <span className="text-[11px] text-zinc-500 uppercase tracking-wide">Priority</span>
                  <div className="flex gap-1">
                    {LEVELS.map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => update(i, { urgency: lvl })}
                        data-testid={`sorted-item-${i}-urgency-${lvl}`}
                        className={`px-2.5 py-1 rounded-lg text-[11px] font-medium capitalize ${
                          it.urgency === lvl
                            ? (lvl === "high" ? "bg-[#991B1B] text-white" : "bg-[#365458] text-white")
                            : "bg-zinc-200 text-zinc-500"
                        }`}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                  <span className="text-[11px] text-zinc-400 ml-auto">~{it.estimated_minutes}m · {it.location}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-5">
            <Button
              data-testid="brain-dump-back-button"
              onClick={() => { setItems(null); setSource(null); }}
              variant="outline"
              className="flex-1 h-14 rounded-2xl bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
            >
              <X className="w-4 h-4 mr-1" /> Edit dump
            </Button>
            <Button
              data-testid="brain-dump-save-button"
              onClick={save}
              disabled={busy || items.length === 0}
              className="flex-[2] h-14 rounded-2xl bg-[#365458] hover:bg-[#2A4346] text-white text-base font-bold"
            >
              Save Items
            </Button>
          </div>
        </div>
      )}
    </PageShell>
  );
}
